import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetUpYourDomainLandingPage = () => {
  const [activeSection, setActiveSection] = useState("quick-actions");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "quick-actions",
        "default-domain",
        "custom-domain",
        "manage-domains",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Your Domain – xPAGE Help Center"
        description="Learn how to set up your domain in xPage. Use your free xPage subdomain or connect your custom domain for a professional online presence."
        keywords="xPAGE, custom domain, xPage domain setup, DNS, eCommerce domain, SSL certificate"
        canonical="https://help.xpage.ai/help/landing-page-setup/Set%20Up%20Your%20Domain"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Set Up Your Domain
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Your domain is the web address customers use to visit your landing
          page. xPage provides a free <strong>xpage.ai</strong> subdomain by
          default (e.g., <em>mystore.xpage.ai</em>), which you can use
          immediately. For a professional, branded experience, you can also
          connect a custom domain you own or purchase a new one through a domain
          provider.
        </p>

        {/* Quick Actions */}
        <section id="quick-actions" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            You can:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Use your default xpage.ai subdomain instantly</li>
              <li>
                Connect a domain you already own from GoDaddy, Namecheap, or
                Google Domains
              </li>
              <li>Purchase a new domain and connect it to xPage</li>
            </ul>
          </div>
        </section>

        {/* Default Domain */}
        <section id="default-domain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Default Domain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              When your landing page is created, xPage assigns a unique
              subdomain such as <em>yourbrand.xpage.shop</em>. This link can be
              shared with customers right away.
              <strong> The default subdomain cannot be changed later</strong>,
              so choose carefully if you plan to use a custom domain.
            </p>
          </div>
        </section>

        {/* Connect a Custom Domain */}
        <section id="custom-domain" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Connect a Custom Domain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              A custom domain strengthens brand trust and recognition. Follow
              these steps:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel mt-3">
              <li className="mb-2">Log in to xPage Admin</li>
              <li className="mb-2">
                Go to{" "}
                <strong>Settings → Domains → Connect Existing Domain</strong>
              </li>
              <li className="mb-2">
                Enter your domain (e.g., <em>www.yourbrand.com</em>) and click{" "}
                <strong>Continue</strong>
              </li>
              <li className="mb-2">
                Add the following DNS records in your registrar:
              </li>
              <ul className="list-disc list-inside ml-6 space-y-1 text-sm">
                <li className="mb-1">
                  <strong>15.197.94.0</strong> — A record
                </li>
                <li className="mb-1">
                  <strong>15.197.169.74</strong> — A record
                </li>
                <li className="mb-1">
                  <strong>CNAME</strong> — Point <code>www</code> to{" "}
                  <code>stores.xpage.ai</code>
                </li>
                <li className="mb-1">
                  Optional A record for root domain (no www)
                </li>
              </ul>
              <li className="mb-2">Save changes in your registrar account</li>
              <li className="mb-2">
                Return to xPage and click <strong>Connect</strong>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> DNS changes can take
                up to 48 hours to propagate.
              </p>
            </div>
          </div>
        </section>

        {/* Manage Connected Domains */}
        <section id="manage-domains" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Manage Connected Domains
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="space-y-1 text-sm list-disc list-inside text-systemLabel">
              <li className="mb-2">
                <strong>Set Primary Domain:</strong> In{" "}
                <em>Settings → Domains</em>, select your preferred domain and
                click <strong>Set as Primary</strong> to control what customers
                see.
              </li>
              <li className="mb-2">
                <strong>Remove a Domain:</strong> Click <strong>Remove</strong>{" "}
                in Domains settings if no longer needed.
              </li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Security Tip:</span> xPage
                provides free SSL certificates for all connected domains,
                ensuring HTTPS for safe browsing. Avoid frequent domain changes
                to protect SEO and always test your domain after connection.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#quick-actions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "quick-actions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Quick Actions
              </a>
            </li>
            <li>
              <a
                href="#default-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "default-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Default Domain
              </a>
            </li>
            <li>
              <a
                href="#custom-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "custom-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Connect Custom Domain
              </a>
            </li>
            <li>
              <a
                href="#manage-domains"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "manage-domains"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Manage Domains
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetUpYourDomainLandingPage;
